import { ENDPOINTS } from '@shared/constants'
import { AuthResponse, User } from '@shared/types'
import { DashboardMetrics } from '../../shared/types/dashboard'

// API is served from the same domain in production via Cloudflare routes
// In development, it can be configured via VITE_API_URL or defaults to relative path (proxy)
const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD 
  ? 'https://adam.lenkostudio.com' 
  : '')

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include', // Important for httpOnly cookies
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    // Prefer server-provided message; otherwise use a safe generic fallback.
    const errorMessage = data.message || data.error || `Request failed (${response.status})`
    const error: any = new Error(errorMessage)
    error.errorCode = data.errorCode
    error.statusCode = response.status
    error.waitTime = data.waitTime // Pass through rate-limit wait time
    error.blockSource = data.blockSource // Pass through block source (ip/account/combined)
    error.attemptsRemaining = data.attemptsRemaining
    error.retryAfterSeconds = data.retryAfterSeconds
    error.expiresInSeconds = data.expiresInSeconds
    throw error
  }

  return response.json()
}

export const authService = {
  async signup(email: string, password: string): Promise<{ success: boolean, message: string }> {
    return fetchApi(ENDPOINTS.AUTH.SIGNUP, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    return fetchApi<AuthResponse>(ENDPOINTS.AUTH.LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  },

  async verifyDeviceCode(
    challengeId: string,
    code: string
  ): Promise<AuthResponse> {
    return fetchApi<AuthResponse>(
      ENDPOINTS.AUTH.VERIFY_DEVICE_CODE,
      {
        method: 'POST',
        body: JSON.stringify({ challengeId, code }),
      }
    )
  },

  async resendDeviceCode(
    challengeId: string
  ): Promise<{
    success: boolean
    message: string
    expiresInSeconds: number
    resendAfterSeconds: number
  }> {
    return fetchApi(
      ENDPOINTS.AUTH.RESEND_DEVICE_CODE,
      {
        method: 'POST',
        body: JSON.stringify({ challengeId }),
      }
    )
  },

  async logout(): Promise<void> {
    await fetchApi(ENDPOINTS.AUTH.LOGOUT, {
      method: 'POST',
    })
  },

  async verifyEmail(token: string): Promise<AuthResponse> {
    return fetchApi<AuthResponse>(ENDPOINTS.AUTH.VERIFY_EMAIL, {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },

  async resendVerification(): Promise<{ success: boolean; message: string }> {
    return fetchApi(ENDPOINTS.AUTH.RESEND_VERIFICATION, {
      method: 'POST',
    })
  },

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await fetchApi<{ user: User }>(ENDPOINTS.AUTH.ME)
      return response.user
    } catch {
      return null
    }
  },

  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    return fetchApi(ENDPOINTS.AUTH.REQUEST_PASSWORD_RESET, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  async validateResetToken(token: string): Promise<{ valid: boolean; message?: string }> {
    return fetchApi(ENDPOINTS.AUTH.VALIDATE_RESET_TOKEN, {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },

  async resetPassword(token: string, newPassword: string): Promise<AuthResponse> {
    return fetchApi<AuthResponse>(ENDPOINTS.AUTH.RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    })
  },

  async getAccounts(): Promise<{ success: boolean; data: any[] }> {
    try {
      const response = await fetchApi<{ success: boolean; accounts: any[] }>(ENDPOINTS.AUTH.ACCOUNTS)
      return { success: true, data: response.accounts }
    } catch (error) {
      console.error('Failed to fetch accounts:', error)
      return { success: false, data: [] }
    }
  },

  async getDashboardData(username: string, period = '7d', forceRefresh = false): Promise<{ success: boolean; data: DashboardMetrics }> {
    try {
      const useCache = forceRefresh ? 'false' : 'true'
      const url = `/api/analytics/dashboard?username=${encodeURIComponent(username)}&period=${period}&use_cache=${useCache}`
      const response = await fetchApi<{ success: boolean; data: DashboardMetrics }>(url)
      return response
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      // Return type-safe mock/null structure if needed, or let error bubble
      throw error 
    }
  },
}
